import React, { useCallback, useContext, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { CartPricingProvider } from '../../../../contexts/CartPricingContext';
import useCheckoutPaths from '../../../../hooks/useCheckoutPaths';
import * as events from '../../../../lib/analytics/events';
import { useCartMode } from '../../../../lib/cartModes';
import CheckoutContext from '../../../../lib/CheckoutContext';
import { getFiGrowthBook } from '../../../../lib/growthbook';
import { priceCartForCheckout } from '../../../../lib/pricing';
import CheckoutSection from '../../../../models/CheckoutSection';
import { cartActions } from '../../../../reducers/storeCart';
import StepContainer from '../../components/StepContainer';
import ConfirmSection from './ConfirmSection';

interface AddressForTaxCalculation {
  country: string;
  postalCode: string;
}

export default function Confirm() {
  const { cart, requiresShippingDetails, session, checkoutState } = useContext(CheckoutContext);
  const { checkoutType } = useCartMode();
  const checkoutPaths = useCheckoutPaths();
  const dispatch = useDispatch();

  const address: AddressForTaxCalculation | undefined = useMemo(() => {
    if (checkoutState.shippingAddress && checkoutState.shippingCode) {
      return {
        country: checkoutState.shippingAddress.country,
        postalCode: checkoutState.shippingAddress.zip,
      };
    }
    return undefined;
  }, [checkoutState.shippingAddress, checkoutState.shippingCode]);

  const fetchPricing = useCallback(async () => {
    const result = await priceCartForCheckout(cart, {
      shippingCode: checkoutState.shippingCode,
      address: address,
      checkoutType,
    });

    if (result.validationErrors?.couponCode) {
      // If the coupon code is invalid, remove it from the cart
      dispatch(cartActions.removeCoupon());

      // By returning undefined here, we won't display the pricing we just fetched which
      // could be stale since the coupon was invalid.
      // The action to remove the coupon from the cart above should cause a re-render,
      // so we will reload the pricing without displaying stale pricing
      return undefined;
    }

    return result;
  }, [cart, checkoutState.shippingCode, address, checkoutType, dispatch]);

  const email = session?.email;
  const missingShipping = requiresShippingDetails && (!checkoutState.shippingAddress || !checkoutState.shippingCode);

  const useStripeCheckout = getFiGrowthBook().getFeatureValue('stripe-elements-checkout', false);
  const fallbackPath = useStripeCheckout ? checkoutPaths.StripeCheckout : checkoutPaths.Shipping;

  if (missingShipping || !email) {
    return <Redirect to={fallbackPath} />;
  } else if (!checkoutState.billingInfo) {
    return <Redirect to={useStripeCheckout ? checkoutPaths.StripeCheckout : checkoutPaths.Payment} />;
  }

  return (
    <StepContainer checkoutSection={CheckoutSection.confirm}>
      <CartPricingProvider fetchPricing={fetchPricing} trackError={(message) => events.confirmation.error(message)}>
        <ConfirmSection email={email} />
      </CartPricingProvider>
    </StepContainer>
  );
}
